import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {AxisLabelsFormatterCallbackFunction, Point, TooltipFormatterCallbackFunction} from 'highcharts';
import {bold, escapeHTML} from 'app/core/helpers/html';
import humanizeDuration from 'humanize-duration';
import {KnownMeasure} from 'app/features/analytics/features/reports/dimension';

@Injectable()
export class I18nextFormattersService {
    // Duration
    private readonly shortHumanizer = humanizeDuration.humanizer({
        language: 'shortEn',
        languages: {
            shortEn: {
                y: () => 'y',
                mo: () => 'mo',
                w: () => 'w',
                d: () => 'd',
                h: () => 'h',
                m: () => 'm',
                s: () => 'sec',
                ms: () => 'ms',
            },
        },
    });

    // arpu
    // arpeu
    private readonly smallRevenueFormatter = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    });
    // revenue
    private readonly revenueFormatter = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    private readonly revenueCompactFormatter = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: 'USD',
        // @ts-ignore
        notation: 'compact',
        // @ts-ignore
        compactDisplay: 'short',
    });
    // percent
    private readonly percentFormatter = new Intl.NumberFormat(this.locale, {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
    // compact view 1m 20k and etc
    private readonly shortNumberFormatter = new Intl.NumberFormat(this.locale, {
        // @ts-ignore
        notation: 'compact',
        // @ts-ignore
        compactDisplay: 'short',
        maximumFractionDigits: 2,
    });
    // dau
    private readonly longNumberFormatter = new Intl.NumberFormat(this.locale, {minimumFractionDigits: 0, maximumFractionDigits: 3});

    private readonly dateTimeFormatter = new Intl.DateTimeFormat(this.locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    });

    constructor(@Inject(LOCALE_ID) public locale: string) {}

    public readonly formatRevenue = v => this.revenueFormatter.format(v);
    public readonly formatRevenueCompact = v => this.revenueCompactFormatter.format(v);
    public readonly formatMicroRevenue = v => this.smallRevenueFormatter.format(v);
    public readonly formatPercent = v => this.percentFormatter.format(v / 100);

    public readonly formatDuration = v =>
        this.shortHumanizer(v * 1000, {maxDecimalPoints: 0})
            .split(' y')
            .join('y')
            .split(' mo')
            .join('mo')
            .split(' w')
            .join('w')
            .split(' d')
            .join('d')
            .split(' h')
            .join('h')
            .split(' m')
            .join('m')
            .split(' sec')
            .join('sec')
            .split(',')
            .join('');

    public readonly formatNumberCompact = v => this.shortNumberFormatter.format(v);
    public readonly formatNumber = v => this.longNumberFormatter.format(v);
    public readonly formatDateTime = v => this.dateTimeFormatter.format(v * 1000);

    public pointFormatter(valueFormatter: (v) => string, withPercent: boolean) {
        const _this = this;
        return function () {
            const point: Point = this;
            let seriesName = escapeHTML(point.series.name);
            if (point.series.chart.hoverSeries === point.series) {
                seriesName = bold(seriesName);
            }
            let result = `<span style="color:${point.color}">\u25CF</span>
${seriesName}: <b>${valueFormatter(point.y)}</b>`;
            if (withPercent) {
                result += `(${_this.formatPercent(point.percentage)})`;
            }
            result += `<br/>`;

            return result;
        };
    }

    getTooltipFormatter(totalLabel: string = 'Total', valueFormatter: (v: any) => string): TooltipFormatterCallbackFunction {
        return function (tooltip) {
            let lines = tooltip.defaultFormatter.call(this, tooltip);

            if (this.points.find(e => !e.percentage)) {
                lines = lines.map(str => str.replace('(NaN%)', ''));
            }

            // @ts-ignore
            const stackedPoint = this.points.find(p => p.total !== undefined);
            if (stackedPoint) {
                const total = valueFormatter(stackedPoint.total);
                lines.splice(1, 0, [`${totalLabel}: <b>${total}</b><br/>`]);
            }
            return lines;
        };
    }

    getMeasureAxisFormatter(measure: string): AxisLabelsFormatterCallbackFunction {
        const formatter = this.getMeasureCompactFormatter(measure);
        return function () {
            return formatter(this.value);
        };
    }

    // TODO: get formatter from metric
    getMeasureFormatter(measure: string): (v: any) => string {
        if (measure.includes('arpu') || measure.includes('arppu') || measure.includes('ecpm') || measure.includes('revenue_per_user')) {
            return this.formatMicroRevenue;
        }

        if (
            (measure.includes('revenue') || measure.includes('spend') || measure.includes('costs')) &&
            !measure.includes(KnownMeasure.SpendReconciliationPer)
        ) {
            return this.formatRevenue;
        }

        if (measure.includes('profit') && !measure.includes('agency_profit_share')) {
            return this.formatRevenue;
        }

        if (measure.includes('payable') || measure.includes('opex')) {
            return this.formatRevenue;
        }

        if (measure.includes(KnownMeasure.Ctr) || measure.includes('roas')) {
            return this.formatPercent;
        }

        switch (measure) {
            case KnownMeasure.Fillrate:
            case KnownMeasure.DisplayRate:
            case KnownMeasure.EngagementRate:
            case KnownMeasure.Roas:
            case KnownMeasure.eRoasToDate:
            case KnownMeasure.eRoasWithAgencyProfitShareToDate:
            case KnownMeasure.eROAS:
            case KnownMeasure.eROASWithAgencyProfitShare:
            case KnownMeasure.RoasActual:
            case KnownMeasure.RoasForecast:
            case KnownMeasure.Ctr:
            case KnownMeasure.CtrUA:
            case KnownMeasure.CvrUA:
            case KnownMeasure.RetentionRate:
            case KnownMeasure.RetentionRateOrganic:
            case KnownMeasure.RetentionRateForecast:
            case KnownMeasure.MediationFeePercent:
            case KnownMeasure.RevisedCtr:
            case KnownMeasure.RevisedClicks:
            case KnownMeasure.RevisedImpressions:
            case KnownMeasure.ATTOptInRate:
            case KnownMeasure.BudgetUsedPercent:
            case KnownMeasure.MediaRoas:
            case KnownMeasure.MediaRoasCalendar:
            case KnownMeasure.MediaRoasCohort:
            case KnownMeasure.MediaRoasForecast:
            case KnownMeasure.Romi:
            case KnownMeasure.RomiToDate:
            case KnownMeasure.RomiCalendar:
            case KnownMeasure.RomiCohort:
            case KnownMeasure.RomiForecast:
            case KnownMeasure.PayingUserConversion:
            case KnownMeasure.ChardonnayRoi:
            case KnownMeasure.ChardonnayRoiToDate:
            case KnownMeasure.ChardonnayRoiCalendar:
            case KnownMeasure.ChardonnayRoiCohort:
            case KnownMeasure.ChardonnayRoiForecast:
            case KnownMeasure.EventConversion:
            case KnownMeasure.EventRatio:
            case KnownMeasure.SpendReconciliationPer:
            case KnownMeasure.IosRoasByNetworkForecast:
            case KnownMeasure.IosRoasBySsotForecast:
            case KnownMeasure.SentryCrashFreeUser:
            case KnownMeasure.SentryCrashFreeSession:
            case KnownMeasure.SentryAnrFreeUser:
            case KnownMeasure.SentryAnrFreeSession:
            case KnownMeasure.SentryNativeFreeUser:
            case KnownMeasure.SentryNativeFreeSession:
            case KnownMeasure.SentryErrorsFreeUser:
            case KnownMeasure.SentryErrorsFreeSession:
            case KnownMeasure.CumulativeConversionToBuyer:
                return this.formatPercent;
            case KnownMeasure.SessionLength:
            case KnownMeasure.AvgTimeSpentPerUser:
            case KnownMeasure.AvgTimeSpentPerUserDaily:
            case KnownMeasure.AvgTimeSpentPerUserMonthly:
            case KnownMeasure.AvgTimeSpentPerUserLifeTime:
            case KnownMeasure.AvgUserSessionLength:
            case KnownMeasure.FullSessionLength:
            case KnownMeasure.FullTimeSpentPerUser:
            case KnownMeasure.AvgFullTimeSpentPerUserLifeTime:
            case KnownMeasure.AvgFullTimeSpentPerUserDaily:
            case KnownMeasure.AvgFullTimeSpentPerUserMonthly:
            case KnownMeasure.TimePerUserDayN:
                return this.formatDuration;
            case KnownMeasure.ManagedRevenue:
            case KnownMeasure.IapRevenue:
            case KnownMeasure.IapRefunds:
            case KnownMeasure.IapRevenueBeforeFees:
            case KnownMeasure.IapRevenueAfterRefunds:
            case KnownMeasure.TotalRevenue:
            case KnownMeasure.CumulativeRevenueForecast:
            case KnownMeasure.CumulativeRevenue:
            case KnownMeasure.eRevenue:
            case KnownMeasure.RevenueForecast:
            case KnownMeasure.SubsRevenue:
            case KnownMeasure.AdRevenue:
            case KnownMeasure.ProfitLoss:
            case KnownMeasure.ProfitLossWithAgencyProfitShare:
            case KnownMeasure.Profit:
            case KnownMeasure.ProfitForecast:
            case KnownMeasure.eProfit:
            case KnownMeasure.eProfitForecastWithAgencyProfitShare:
            case KnownMeasure.AgencyProfitShare:
            case KnownMeasure.AgencyProfitShareCalendar:
            case KnownMeasure.AgencyProfitShareCohort:
            case KnownMeasure.Spend:
            case KnownMeasure.SpendDirect:
            case KnownMeasure.SpendReconciliation:
            case KnownMeasure.Cpi:
            case KnownMeasure.CostPerNetworkInstall:
            case KnownMeasure.MediationFee:
            case KnownMeasure.DemandFee:
            case KnownMeasure.TotalFee:
            case KnownMeasure.AttributionCosts:
            case KnownMeasure.ChardonnayExpenses:
            case KnownMeasure.ChardonnayExpensesCalendar:
            case KnownMeasure.ChardonnayExpensesCohort:
            case KnownMeasure.EventRevenue:
            case KnownMeasure.CumulativeCostPerBuyer:
            case KnownMeasure.Cpm:
                return this.formatRevenue;
            case KnownMeasure.Arpeu:
            case KnownMeasure.eLTV:
            case KnownMeasure.AdArpu:
            case KnownMeasure.ARPDAU:
            case KnownMeasure.IAP_ARPDAU:
            case KnownMeasure.TOTAL_ARPDAU:
            case KnownMeasure.ManagedARPDAU:
            case KnownMeasure.TotalManagedARPDAU:
            case KnownMeasure.AFPDAU:
            case KnownMeasure.AFPU:
            case KnownMeasure.Ecpm:
            case KnownMeasure.RevisedEcpm:
            case KnownMeasure.ManagedEcpm:
                return this.formatMicroRevenue;
            case KnownMeasure.ActiveUsersPerAd:
            case KnownMeasure.DAU:
            case KnownMeasure.AdEngagedUsers:
            case KnownMeasure.Installs:
            case KnownMeasure.eInstalls:
                return this.formatNumber;
            default:
                return this.formatNumber;
        }
    }

    // TODO: get formatter from metric
    getMeasureCompactFormatter(measure: string): (v: any) => string {
        const regularFormatter = this.getMeasureFormatter(measure);

        switch (regularFormatter) {
            case this.formatRevenue:
                return this.formatRevenueCompact;
            case this.formatNumber:
                return this.formatNumberCompact;
            default:
                return regularFormatter;
        }
    }
}
